import './CompanyDocumentViewer.css';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import DocxPreview, { DOCX_MIME } from '../components/DocxPreview';
import { RichTextTermsContent } from '../components/RichTextTerms';

export interface CompanyDocumentViewerRequest {
  key: string;
  title: string;
  fileName: string;
  mimeType?: string | null;
  formattedTerms?: boolean;
  unavailableMessage?: string;
  load: () => Promise<Blob>;
}

interface CompanyDocumentViewerState {
  request: CompanyDocumentViewerRequest;
  blob: Blob | null;
  loading: boolean;
  error: string | null;
}

const extensionMimeTypes: Record<string, string> = {
  docx: DOCX_MIME,
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  txt: 'text/plain'
};

const documentKind = (mimeType: string, fileName: string) => {
  const normalized = mimeType.toLowerCase().split(';')[0].trim();
  if (normalized === DOCX_MIME) return 'docx';
  if (normalized.startsWith('image/')) return 'image';
  if (normalized === 'application/pdf') return 'pdf';
  if (normalized.startsWith('text/')) return 'text';

  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
  const inferred = extensionMimeTypes[extension];
  if (inferred === DOCX_MIME) return 'docx';
  if (inferred?.startsWith('image/')) return 'image';
  if (inferred === 'application/pdf') return 'pdf';
  if (inferred?.startsWith('text/')) return 'text';
  return 'fallback';
};

const resolvedMimeType = (
  request: CompanyDocumentViewerRequest,
  blob: Blob
) => {
  if (blob.type && blob.type !== 'application/octet-stream') return blob.type;
  if (request.mimeType) return request.mimeType;
  const extension = request.fileName.split('.').pop()?.toLowerCase() ?? '';
  return extensionMimeTypes[extension] ?? 'application/octet-stream';
};

export const useCompanyDocumentViewer = () => {
  const sequence = useRef(0);
  const [state, setState] = useState<CompanyDocumentViewerState | null>(null);

  const openDocument = useCallback(
    async (request: CompanyDocumentViewerRequest) => {
      const requestSequence = ++sequence.current;
      if (request.unavailableMessage) {
        setState({
          request,
          blob: null,
          loading: false,
          error: request.unavailableMessage
        });
        return;
      }
      setState({ request, blob: null, loading: true, error: null });
      try {
        const blob = await request.load();
        if (requestSequence !== sequence.current) return;
        setState({ request, blob, loading: false, error: null });
      } catch (error) {
        if (requestSequence !== sequence.current) return;
        setState({
          request,
          blob: null,
          loading: false,
          error:
            (error as { response?: { status?: number } })?.response?.status ===
            404
              ? 'This stored file is unavailable. Upload a replacement to restore preview and download access.'
              : 'The stored document could not be opened. Try again, or upload a replacement if the problem continues.'
        });
      }
    },
    []
  );

  const closeDocument = useCallback(() => {
    sequence.current += 1;
    setState(null);
  }, []);

  return {
    state,
    openDocument,
    closeDocument,
    openingKey: state?.loading ? state.request.key : null
  };
};

export const CompanyDocumentViewerDialog = ({
  state,
  onClose,
  actions
}: {
  state: CompanyDocumentViewerState | null;
  onClose: () => void;
  actions?: ReactNode;
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  const mimeType = useMemo(
    () =>
      state?.blob && state.request
        ? resolvedMimeType(state.request, state.blob)
        : state?.request.mimeType ?? 'application/octet-stream',
    [state]
  );
  const kind = state ? documentKind(mimeType, state.request.fileName) : null;

  useEffect(() => {
    setRenderError(null);
    setTextContent(null);
    if (!state?.blob) {
      setObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(
      state.blob.slice(0, state.blob.size, mimeType)
    );
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [state?.blob, mimeType]);

  useEffect(() => {
    if (kind !== 'text' || !state?.blob) return undefined;
    let active = true;
    state.blob
      .text()
      .then((value) => active && setTextContent(value))
      .catch(
        () =>
          active && setRenderError('The text document could not be rendered.')
      );
    return () => {
      active = false;
    };
  }, [kind, state?.blob]);

  const download = () => {
    if (!state?.blob || !objectUrl) return;
    const anchor = window.document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = state.request.fileName;
    anchor.click();
  };

  return (
    <Dialog
      open={Boolean(state)}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="company-document-viewer-title"
    >
      <DialogTitle id="company-document-viewer-title">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            color="inherit"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={onClose}
          >
            Back
          </Button>
          <span className="profile-review-document-viewer-title">
            <strong>{state?.request.title ?? 'Document Viewer'}</strong>
            {state?.request.fileName && <small>{state.request.fileName}</small>}
          </span>
        </Stack>
      </DialogTitle>
      <DialogContent className="profile-review-document-viewer-content">
        {state?.loading ? (
          <div className="profile-review-document-viewer-status" role="status">
            <CircularProgress size={34} />
            <strong>Opening Stored Document…</strong>
          </div>
        ) : state?.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : renderError ? (
          <Alert severity="error">{renderError}</Alert>
        ) : kind === 'image' && objectUrl ? (
          <div className="profile-review-document-image-stage">
            <img
              src={objectUrl}
              alt={state?.request.title ?? 'Stored document'}
              onError={() =>
                setRenderError('The image document could not be rendered.')
              }
            />
          </div>
        ) : kind === 'docx' && state?.blob ? (
          <div style={{ height: '68vh' }}>
            <DocxPreview
              blob={state.blob}
              title={`${state.request.title} preview`}
            />
          </div>
        ) : kind === 'pdf' && objectUrl ? (
          <iframe
            className="profile-review-document-frame"
            title={`${state?.request.title ?? 'Stored document'} preview`}
            src={objectUrl}
          />
        ) : kind === 'text' && state?.request.formattedTerms ? (
          <div className="profile-review-formatted-document">
            <RichTextTermsContent value={textContent ?? ''} />
          </div>
        ) : kind === 'text' ? (
          <pre className="profile-review-document-text">
            {textContent ?? 'Loading document text…'}
          </pre>
        ) : state?.blob ? (
          <div className="profile-review-document-viewer-status">
            <strong>Preview Not Available For This File Type</strong>
            <p>
              {state.request.fileName} is stored and available. Download it to
              open it in its native application.
            </p>
            <small>{mimeType || 'Unknown file type'}</small>
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        {actions}
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={!state?.blob || !objectUrl}
          onClick={download}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
